import type { ProsthesisStatus } from "@bookdental/schemas";

export const STATUS_META: Record<
  ProsthesisStatus,
  { label: string; color: string; bg: string }
> = {
  AGUARDANDO_CONFIRMACAO: { label: "Aguardando Confirmação", color: "#2457ff", bg: "#2457ff1a" },
  RECUSADO: { label: "Recusado", color: "#78716c", bg: "#78716c1a" },
  ACEITO: { label: "Aceito", color: "#6d95ff", bg: "#6d95ff1a" },
  EM_PRODUCAO: { label: "Em produção", color: "#f59e0b", bg: "#f59e0b1a" },
  CONCLUIDO: { label: "Concluído", color: "#22c55e", bg: "#22c55e1a" },
  CANCELADO: { label: "Cancelado", color: "#ef4444", bg: "#ef44441a" },
};

/**
 * Status que encerram a vida do serviço — nada mais acontece com ele por conta própria.
 *
 * Governa três coisas na tela: "Finalizar" some (não há o que finalizar de novo), "Editar serviço"
 * some (alterar o que já terminou reescreve o passado) e só CONCLUIDO oferece "Refazer" — não se
 * refaz o que nunca foi feito.
 */
export const STATUS_TERMINAIS: ProsthesisStatus[] = ["CONCLUIDO", "CANCELADO", "RECUSADO"];

export const STATUS_ORDER: ProsthesisStatus[] = [
  "AGUARDANDO_CONFIRMACAO",
  "RECUSADO",
  "ACEITO",
  "EM_PRODUCAO",
  "CONCLUIDO",
  "CANCELADO",
];

export type StatusPhaseKey = "recebido" | "producao" | "finalizado";

/** Segmentação por fase — fonte única usada pelas pills de filtro e pelos cards do dashboard. O
 *  detalhe de bancada (o que era "Vazar gesso"/"Fresando"/etc) vive em ProductionStage, não aqui. */
export const STATUS_PHASES: { key: StatusPhaseKey; label: string; accent: string; statuses: ProsthesisStatus[] }[] = [
  { key: "recebido", label: "Recebido", accent: "#2457ff", statuses: ["ACEITO"] },
  { key: "producao", label: "Produção", accent: "#f59e0b", statuses: ["EM_PRODUCAO"] },
  { key: "finalizado", label: "Finalizado", accent: "#22c55e", statuses: ["CONCLUIDO", "CANCELADO"] },
];

/** Status que encerram o pedido (não contam como "em aberto"/atrasado). */
export const TERMINAL_STATUSES: ProsthesisStatus[] = ["CONCLUIDO", "CANCELADO", "RECUSADO"];

/** Formata centavos → "R$ 1.234,56". Retorna "—" se null. */
export function formatBRL(cents: number | null | undefined): string {
  if (cents == null) return "—";
  return (cents / 100).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

/** Formata Date → "dd/mm/aaaa". Retorna "—" se null. */
export function formatDate(value: Date | string | null | undefined): string {
  if (!value) return "—";
  const d = typeof value === "string" ? new Date(value) : value;
  return d.toLocaleDateString("pt-BR");
}

/** Serviço único mostra o rótulo completo (+ dentes/região se tiver); mais de um vira contagem —
 *  versão leve pra resumos compactos (dialog de resumo, histórico da ficha do paciente), sem
 *  acoplar ao tipo pesado de serviço usado nas telas de produção (`orderServicesSummary` em
 *  production-types.ts). */
export function orderServiceSummary(
  services: { serviceCategory: { label: string }; teeth: string | null }[],
): string {
  if (services.length === 0) return "—";
  if (services.length === 1) {
    const s = services[0];
    return s.teeth ? `${s.serviceCategory.label} (${s.teeth})` : s.serviceCategory.label;
  }
  return `${services.length} serviços`;
}
